import React, { useState, useEffect, useRef } from 'react';
import { Trash2, Plus, X, Edit2, Save, RotateCcw, Eye, EyeOff, Copy, Check } from 'lucide-react';
import api from '../api/axios';
import { useTranslation } from 'react-i18next';
import AttendanceSparkLine from "../components/AttendanceSparkLines";
import AttendanceBars from "../components/AttendanceBars";
import Table from "../components/Table";

const generatePassword = (length = 12) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*';
  let result = '';

  for (let i = 0; i < length; i += 1) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return result;
};

export default function UsersPage() {
  const { t } = useTranslation();

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // For ADD modal
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    systemId: '',
    phoneNumber: '',
    roleId: '',
    workingHours: '',
    password: '',
    confirmPassword: '',
    // hourlyRate: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [createdUserInfo, setCreatedUserInfo] = useState(null);
  const [copiedKey, setCopiedKey] = useState('');
  const copiedResetTimerRef = useRef(null);

  // For inline editing
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Fetch users + roles
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const companyId = localStorage.getItem("companyId");

        const [usersRes, rolesRes] = await Promise.all([
          api.get(`/companies/${companyId}/users`),
          api.get(`/companies/${companyId}/roles`)
        ]);

        setUsers(usersRes.data?.data || []);
        setRoles(rolesRes.data?.data || []);
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.message || t('failedToLoadData'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    return () => {
      if (copiedResetTimerRef.current) {
        clearTimeout(copiedResetTimerRef.current);
      }
    };
  }, []);

  // ─────────────────────────────────────────────
  //          DELETE
  // ─────────────────────────────────────────────
  const handleDelete = async (userId) => {
    if (!window.confirm(t('confirmDeleteUser'))) return;

    try {
      await api.delete(`/users/${userId}`);
      setUsers((prev) => prev.filter((u) => u._id !== userId));
      if (editingId === userId) setEditingId(null);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || t('failedToDeleteUser'));
    }
  };

  // ─────────────────────────────────────────────
  //          START EDITING
  // ─────────────────────────────────────────────
  const startEdit = (user) => {
    setEditingId(user._id);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      systemId: user.systemId || '',
      phoneNumber: user.phoneNumber || '',
      roleId: user.role?._id || user.role || '',
      workingHours: user.workingHours?.toString() || '',
      // hourlyRate: user.hourlyRate?.toString() || '0',
    });
  };

  // ─────────────────────────────────────────────
  //          EDIT FORM CHANGE
  // ─────────────────────────────────────────────
  const handleEditChange = (e) => {
    setEditForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // ─────────────────────────────────────────────
  //          SAVE (PUT)
  // ─────────────────────────────────────────────
  const handleSave = async (userId) => {
    try {
      const payload = {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        systemId: editForm.systemId.trim(),
        phoneNumber: editForm.phoneNumber.trim(),
        roleId: editForm.roleId || null,
        workingHours: Number(editForm.workingHours) || 8,
        // hourlyRate: Number(editForm.hourlyRate) || 0,
      };

      const res = await api.put(`/users/${userId}`, payload);

      setUsers((prev) =>
        prev.map((u) =>
          u._id === userId ? { ...u, ...res.data?.data || payload } : u
        )
      );

      setEditingId(null);
      setEditForm({});
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || t('failedToUpdateUser'));
    }
  };

  // ─────────────────────────────────────────────
  //          CANCEL EDIT
  // ─────────────────────────────────────────────
  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  // ─────────────────────────────────────────────
  //          ADD USER
  // ─────────────────────────────────────────────
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const openAddModal = () => {
    const generatedPassword = generatePassword();
    setForm({
      firstName: '',
      lastName: '',
      systemId: '',
      phoneNumber: '',
      roleId: '',
      workingHours: '',
      password: generatedPassword,
      confirmPassword: generatedPassword,
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    setIsOpen(true);
  };

  const closeAddModal = () => {
    setIsOpen(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const copyText = async (value) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (err) {
      console.error(err);
      alert(t('failedToCopy') || 'Failed to copy');
      return false;
    }
  };

  const handleCopyClick = async (key, value) => {
    const copied = await copyText(value);
    if (!copied) return;

    setCopiedKey(key);

    if (copiedResetTimerRef.current) {
      clearTimeout(copiedResetTimerRef.current);
    }

    copiedResetTimerRef.current = setTimeout(() => {
      setCopiedKey('');
    }, 1200);
  };

  const handleAddUser = async () => {
    if (!form.firstName || !form.lastName || !form.systemId || !form.password || !form.confirmPassword) {
      alert(t('requiredFieldsMissing'));
      return;
    }

    if (form.password !== form.confirmPassword) {
      alert(t('passwordsDoNotMatch') || 'Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      const createdPassword = form.password;

      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        systemId: form.systemId.trim(),
        phoneNumber: form.phoneNumber.trim(),
        roleId: form.roleId || null,
        workingHours: Number(form.workingHours) || 8,
        password: createdPassword,
        // hourlyRate: Number(form.hourlyRate) || 0,
      };

      const companyId = localStorage.getItem("companyId");
      const response = await api.post(`/users/company/${companyId}`, payload);

      const newUser = response.data?.data || response.data;
      setUsers((prev) => [...prev, newUser]);
      setCreatedUserInfo({
        uniqueId: newUser?.uniqueId || '',
        password: createdPassword,
        firstName: newUser?.firstName || payload.firstName,
        lastName: newUser?.lastName || payload.lastName,
      });
      closeAddModal();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || t('failedToAddUser'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-300">{t('loadingUsers')}</div>;
  if (error) return <div className="p-10 text-center text-red-400">{t('error')}: {error}</div>;

  return (
    <div className="p-6 text-white">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-blue-400">{t('usersTitle')}</h1>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-xl shadow-lg shadow-sky-900/20 transition-all duration-200 active:scale-95"
        >
          <Plus size={18} /> {t('addUser')}
        </button>
      </div>

      {/* Users Table */}
      <div className="bg-gray-800 rounded-2xl shadow overflow-auto">
        <Table
          data={users}
          columns={[
            { header: t('id'), accessor: 'uniqueId', sortable: true },
            { 
              header: t('name'), 
              sortAccessor: 'firstName', 
              sortable: true,
              render: (user) => editingId === user._id ? (
                <input
                  name="firstName"
                  value={editForm.firstName}
                  onChange={handleEditChange}
                  className="w-full bg-gray-700 text-white px-2 py-1 rounded border border-gray-600"
                />
              ) : user.firstName
            },
            {
              header: t('surname'),
              sortAccessor: 'lastName',
              sortable: true,
              render: (user) => editingId === user._id ? (
                <input
                  name="lastName"
                  value={editForm.lastName}
                  onChange={handleEditChange}
                  className="w-full bg-gray-700 text-white px-2 py-1 rounded border border-gray-600"
                />
              ) : user.lastName
            },
            {
              header: t('phoneNumber'),
              sortAccessor: 'phoneNumber',
              sortable: true,
              render: (user) => editingId === user._id ? (
                <input
                  name="phoneNumber"
                  value={editForm.phoneNumber}
                  onChange={handleEditChange}
                  className="w-full bg-gray-700 text-white px-2 py-1 rounded border border-gray-600"
                />
              ) : (user.phoneNumber || '—')
            },
            {
              header: t('systemId'),
              sortAccessor: 'systemId',
              sortable: true,
              render: (user) => editingId === user._id ? (
                <input
                  name="systemId"
                  value={editForm.systemId}
                  onChange={handleEditChange}
                  className="w-full bg-gray-700 text-white px-2 py-1 rounded border border-gray-600"
                />
              ) : (user.systemId || '—')
            },
            {
              header: t('positionTitle'),
              sortAccessor: (row) => row.role ? (typeof row.role === 'object' ? row.role.name : row.role) : (row.positionTitle || '—'),
              sortable: true,
              render: (user) => {
                const displayedRole = user.role
                  ? (typeof user.role === 'object' ? user.role.name : user.role)
                  : (user.positionTitle || '—');
                
                return editingId === user._id ? (
                  <select
                    name="roleId"
                    value={editForm.roleId}
                    onChange={handleEditChange}
                    className="w-full bg-gray-700 text-white px-2 py-1 rounded border border-gray-600"
                  >
                    <option value="">—</option>
                    {roles.map(role => (
                      <option key={role._id} value={role._id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                ) : displayedRole;
              }
            },
            {
              header: t('workingHoursDay'),
              sortAccessor: 'workingHours',
              sortable: true,
              render: (user) => editingId === user._id ? (
                <input
                  type="number"
                  name="workingHours"
                  value={editForm.workingHours}
                  onChange={handleEditChange}
                  className="w-full bg-gray-700 text-white px-2 py-1 rounded border border-gray-600"
                />
              ) : (user.workingHours || '—')
            },
            {
              header: t('lastSevenStats'),
              render: (user) => <AttendanceSparkLine summaries={user.associateSummaries} />
            },
            {
              header: t('lastSevenAttendance'),
              render: (user) => <AttendanceBars events={user.events} />
            },
            {
              header: t('actions'),
              align: 'center',
              render: (user) => editingId === user._id ? (
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => handleSave(user._id)}
                    className="text-green-500 hover:text-green-400 transition"
                    title="Save"
                  >
                    <Save size={18} />
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="text-gray-400 hover:text-gray-200 transition"
                    title="Cancel"
                  >
                    <RotateCcw size={18} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => startEdit(user)}
                    className="text-blue-400 hover:text-blue-300 transition"
                    title="Edit"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(user._id)}
                    className="text-red-500 hover:text-red-400 transition"
                    disabled={submitting}
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              )
            }
          ]}
          headerClassName="bg-gray-700 text-gray-300"
          rowClassName="border-t border-gray-700 hover:bg-gray-900 transition"
          keyExtractor={(user) => user._id}
          emptyMessage={t('noUsersFound')}
        />
      </div>

      {/* Add User Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-md shadow-lg relative">
            <button
              onClick={closeAddModal}
              className="absolute top-3 right-3 text-gray-400 hover:text-white"
              disabled={submitting}
            >
              <X size={20} />
            </button>

            <h2 className="text-xl font-semibold mb-4">{t('addNewUser')}</h2>

            <div className="space-y-3">
              <input
                name="firstName"
                placeholder={t('firstName')}
                value={form.firstName}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white outline-none"
                disabled={submitting}
              />
              <input
                name="lastName"
                placeholder={t('lastName')}
                value={form.lastName}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white outline-none"
                disabled={submitting}
              />
              <input
                name="phoneNumber"
                placeholder={t('phoneNumber')}
                value={form.phoneNumber}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white outline-none"
                disabled={submitting}
              />
              <select
                name="roleId"
                value={form.roleId}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white outline-none"
                disabled={submitting}
              >
                <option value="">{t('selectRole') || 'Select role...'}</option>
                {roles.map(role => (
                  <option key={role._id} value={role._id}>
                    {role.name}
                  </option>
                ))}
              </select>
              <input
                name="systemId"
                placeholder={t('systemId')}
                value={form.systemId}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white outline-none"
                disabled={submitting}
              />
              <input
                type="number"
                name="workingHours"
                placeholder={t('workingHoursPlaceholder')}
                value={form.workingHours}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white outline-none"
                disabled={submitting}
              />

              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder={t('password') || 'Password'}
                  value={form.password}
                  onChange={handleChange}
                  className="w-full px-3 py-2 pr-20 rounded-lg bg-gray-700 text-white outline-none"
                  disabled={submitting}
                />
                <div className="absolute inset-y-0 right-2 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="text-gray-300 hover:text-white"
                    title={showPassword ? (t('hidePassword') || 'Hide password') : (t('showPassword') || 'Show password')}
                    disabled={submitting}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCopyClick('password', form.password)}
                    className={`rounded-md p-1 transition-all duration-200 active:scale-90 ${copiedKey === 'password' ? 'text-emerald-400 bg-emerald-500/15' : 'text-gray-300 hover:text-white hover:bg-white/10'}`}
                    title={t('copy') || 'Copy'}
                    disabled={submitting}
                  >
                    {copiedKey === 'password' ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>

              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  placeholder={t('confirmPassword') || 'Confirm password'}
                  value={form.confirmPassword}
                  onChange={handleChange}
                  className="w-full px-3 py-2 pr-20 rounded-lg bg-gray-700 text-white outline-none"
                  disabled={submitting}
                />
                <div className="absolute inset-y-0 right-2 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="text-gray-300 hover:text-white"
                    title={showConfirmPassword ? (t('hidePassword') || 'Hide password') : (t('showPassword') || 'Show password')}
                    disabled={submitting}
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCopyClick('confirmPassword', form.confirmPassword)}
                    className={`rounded-md p-1 transition-all duration-200 active:scale-90 ${copiedKey === 'confirmPassword' ? 'text-emerald-400 bg-emerald-500/15' : 'text-gray-300 hover:text-white hover:bg-white/10'}`}
                    title={t('copy') || 'Copy'}
                    disabled={submitting}
                  >
                    {copiedKey === 'confirmPassword' ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={handleAddUser}
              disabled={submitting}
              className={`mt-5 w-full py-2 rounded-xl font-semibold transition ${submitting ? 'bg-blue-800 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {submitting ? t('adding') : t('addUser')}
            </button>
          </div>
        </div>
      )}

      {createdUserInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-md shadow-lg">
            <h2 className="text-xl font-semibold mb-4">{t('userCreated') || 'User created'}</h2>
            <p className="text-gray-200 mb-2">
              {(t('userCreatedWithName')).replace('{{name}}', `${createdUserInfo.firstName} ${createdUserInfo.lastName}`.trim())}
            </p>
            <p className="text-gray-300 mb-1">{t('loginLabel') || 'Login'}: <span className="font-semibold text-white">{createdUserInfo.uniqueId || '-'}</span></p>
            <p className="text-gray-300 mb-6">{t('password') || 'Password'}: <span className="font-semibold text-white">{createdUserInfo.password}</span></p>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => handleCopyClick('credentials', `Login: ${createdUserInfo.uniqueId || '-'} | Password: ${createdUserInfo.password}`)}
                className={`px-4 py-2 rounded-xl text-white transition-all duration-200 active:scale-95 ${copiedKey === 'credentials' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-sky-600 hover:bg-sky-500'}`}
              >
                <span className="inline-flex items-center gap-2">
                  {copiedKey === 'credentials' ? <Check size={16} /> : <Copy size={16} />}
                  {copiedKey === 'credentials' ? (t('copied') || 'Copied') : (t('copy') || 'Copy')}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setCreatedUserInfo(null)}
                className="px-4 py-2 rounded-xl bg-gray-700 hover:bg-gray-600 text-white"
              >
                {t('close') || 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}